import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Calendar, Clock, MapPin, X } from "lucide-react";
import { fetchPopupEvent } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PopupEventModal() {
  const [open, setOpen] = useState(false);
  const [event, setEvent] = useState(null);
  const { pathname } = useLocation();
  const suppressed = ["/apply", "/privacy"].includes(pathname);

  useEffect(() => {
    let cancelled = false;
    fetchPopupEvent()
      .then((e) => {
        if (cancelled || !e) return;
        setEvent(e);
        setTimeout(() => setOpen(true), 800);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!event) return null;

  const imgSrc = event.image_url
    ? (event.image_url.startsWith("http") ? event.image_url : `${BACKEND_URL}${event.image_url}`)
    : null;

  return (
    <Dialog open={open && !suppressed} onOpenChange={setOpen}>
      <DialogContent
        data-testid="tea-party-modal"
        className="p-0 overflow-hidden max-w-lg w-[calc(100%-2rem)] rounded-3xl border-brand-line bg-white"
      >
        <DialogTitle className="sr-only">{event.title}</DialogTitle>
        <DialogDescription className="sr-only">{event.description}</DialogDescription>
        <button
          aria-label="Close"
          data-testid="tea-party-modal-close"
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur rounded-full p-2 hover:bg-white transition"
        >
          <X className="w-4 h-4 text-brand-charcoal" />
        </button>

        {imgSrc && (
          <div className="relative h-48 sm:h-56 overflow-hidden">
            <img src={imgSrc} alt={event.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white"></div>
            <div className="absolute bottom-3 left-6">
              <span className="inline-block text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold bg-white/80 backdrop-blur px-3 py-1 rounded-full">
                You're Invited
              </span>
            </div>
          </div>
        )}

        <div className="px-6 sm:px-8 pb-8 pt-4">
          <h2 className="font-serif text-3xl sm:text-4xl text-brand-charcoal leading-tight">{event.title}</h2>
          <p className="font-sans text-[15px] text-brand-muted mt-3 leading-relaxed">{event.description}</p>

          <div className="mt-5 space-y-2.5 text-sm text-brand-charcoal">
            {event.date_label && (
              <div className="flex items-center gap-3"><Calendar className="w-4 h-4 text-brand-teal" /><span>{event.date_label}</span></div>
            )}
            {event.time_label && (
              <div className="flex items-center gap-3"><Clock className="w-4 h-4 text-brand-teal" /><span>{event.time_label}</span></div>
            )}
            {event.location && (
              <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-brand-teal" /><span className="text-brand-muted">{event.location}</span></div>
            )}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            {event.rsvp_url && (
              <a
                href={event.rsvp_url}
                target="_blank"
                rel="noreferrer"
                data-testid="tea-party-modal-rsvp"
                className="inline-flex items-center justify-center bg-brand-teal text-white rounded-full px-6 py-3 font-medium hover:bg-brand-tealDark transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-md"
              >
                Reserve Your Spot
              </a>
            )}
            <button
              onClick={() => setOpen(false)}
              data-testid="tea-party-modal-dismiss"
              className="inline-flex items-center justify-center border border-brand-line text-brand-muted rounded-full px-6 py-3 font-medium hover:border-brand-teal hover:text-brand-teal transition-colors"
            >
              Maybe later
            </button>
          </div>
          <p className="text-[11px] text-brand-muted mt-5">Questions? Call or text (918) 316-5889</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
